// ── Portal Context ──
// Provides tenant-resolved branding, config, and communication state
// for the white-labeled customer portal.

import { createContext, useContext, useState, useMemo, useCallback, type ReactNode } from "react";
import type {
  TenantPortalBranding,
  TenantPortalConfig,
  PortalNotification,
  PortalThreadMessage,
  NotificationTrigger,
  PortalFeatureKey,
} from "./types";
import {
  resolveBranding,
  resolveConfig,
  generatePortalNotification,
  buildSystemThreadMessage,
  isFeatureEnabled,
} from "./engine";
import type { Job } from "@/data/types";

interface PortalContextValue {
  /** Resolved tenant branding */
  branding: TenantPortalBranding | null;
  /** Resolved tenant config */
  config: TenantPortalConfig | null;
  /** Active tenant ID for the portal */
  tenantId: string;
  /** Check if a portal feature is enabled */
  hasFeature: (feature: PortalFeatureKey) => boolean;
  /** Notification history */
  notifications: PortalNotification[];
  /** Send a notification */
  sendNotification: (trigger: NotificationTrigger, job: Job, extraVars?: Record<string, string>) => void;
  /** Communication thread messages */
  threadMessages: PortalThreadMessage[];
  /** Add a thread message */
  addThreadMessage: (msg: PortalThreadMessage) => void;
  /** Add a system message to a job thread */
  addSystemMessage: (jobId: string, body: string) => void;
}

const PortalContext = createContext<PortalContextValue | null>(null);

interface PortalProviderProps {
  tenantId: string;
  children: ReactNode;
}

export function PortalProvider({ tenantId, children }: PortalProviderProps) {
  const branding = useMemo(() => resolveBranding(tenantId) ?? null, [tenantId]);
  const config = useMemo(() => resolveConfig(tenantId) ?? null, [tenantId]);
  const [notifications, setNotifications] = useState<PortalNotification[]>([]);
  const [threadMessages, setThreadMessages] = useState<PortalThreadMessage[]>([]);

  const hasFeature = useCallback(
    (feature: PortalFeatureKey) => isFeatureEnabled(tenantId, feature),
    [tenantId]
  );

  const sendNotification = useCallback(
    (trigger: NotificationTrigger, job: Job, extraVars?: Record<string, string>) => {
      const newNotifs = generatePortalNotification(tenantId, trigger, job, extraVars);
      if (newNotifs.length) {
        setNotifications(prev => [...prev, ...newNotifs]);
        // Also add system thread messages for each notification
        newNotifs.forEach(n => {
          const sysMsg = buildSystemThreadMessage(tenantId, n.jobId, `📬 ${n.subject}`);
          setThreadMessages(prev => [...prev, sysMsg]);
        });
      }
    },
    [tenantId]
  );

  const addThreadMessage = useCallback((msg: PortalThreadMessage) => {
    setThreadMessages(prev => [...prev, msg]);
  }, []);

  const addSystemMessage = useCallback(
    (jobId: string, body: string) => {
      const msg = buildSystemThreadMessage(tenantId, jobId, body);
      setThreadMessages(prev => [...prev, msg]);
    },
    [tenantId]
  );

  return (
    <PortalContext.Provider
      value={{
        branding,
        config,
        tenantId,
        hasFeature,
        notifications,
        sendNotification,
        threadMessages,
        addThreadMessage,
        addSystemMessage,
      }}
    >
      {children}
    </PortalContext.Provider>
  );
}

export function usePortal() {
  const ctx = useContext(PortalContext);
  if (!ctx) throw new Error("usePortal must be used within PortalProvider");
  return ctx;
}
